export interface EventRoadmapItem {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  quarter?: string;
  dateRange?: string;
  status: 'completed' | 'active' | 'upcoming' | 'classified';
  badge: string;
  description: string;
  thumbnail?: string;
  location?: string;
  highlights?: string[];
  galleryCount?: number;
}

export interface EventGalleryImage {
  src: string;
  thumb?: string;
  title: string;
  category: 'citadel' | 'strike' | 'core' | 'architecture';
  caption: string;
  photographer?: string;
}

export interface EventDetail {
  id: string;
  title: string;
  tagline: string;
  date: string;
  status: string;
  heroImage: string;
  overview: string[];
  stats: { label: string; value: string; icon: string }[];
  lore: { heading: string; text: string; icon: string }[];
  gallery: EventGalleryImage[];
}

export const EVENT_ROADMAP: EventRoadmapItem[] = [
  {
    id: "halloween-2026",
    slug: "halloween-2026",
    title: "Halloween 2026: Haunted Ocean Realm",
    subtitle: "The Sovereign Halloween Festival & Build Championship",
    quarter: "October 2026",
    dateRange: "Preserved Forever",
    status: "completed",
    badge: "Official Preserved Realm",
    description: "A monumental Halloween festival successfully hosted across the abyssal ocean featuring 10 builder colosseums, gothic blackstone altars, and an ominous celestial encounter.",
    thumbnail: "/assets/events/halloween-2026/2026-11-01_22.44.24_4K.webp",
    location: "Ocean Coordinates: X: 7000, Y: 64, Z: 6500",
    highlights: [
      "10 Dedicated 32x32 Player Building Plots",
      "Gothic Blackstone Altar & Witch Cauldrons",
      "Deep Ocean Bed Support Foundations (Y: 25)",
      "Triumphant Halloween Festival & Community Gathering",
      "Permanent In-Game Teleportation via Event Manager"
    ],
    galleryCount: 13
  },
  {
    id: "mystery-phase-2",
    slug: "classified-operation-02",
    title: "Classified Event Operation",
    subtitle: "High Council Classified Directive",
    status: "classified",
    badge: "Encrypted • Phase II",
    description: "Deep subterranean seismic telemetry and atmospheric monitoring indicate impending high-order anomalies. Details locked under sovereign encryption."
  },
  {
    id: "mystery-phase-3",
    slug: "classified-operation-03",
    title: "Classified Event Operation",
    subtitle: "Frontier Milestone Protocol",
    status: "classified",
    badge: "Encrypted • Phase III",
    description: "Strategic community milestone currently under confidential planning by the High Council and server engineering architecture."
  },
  {
    id: "mystery-phase-4",
    slug: "classified-operation-04",
    title: "Classified Event Operation",
    subtitle: "Grand Horizon Spectacle",
    status: "classified",
    badge: "Encrypted • Phase IV",
    description: "Long-term sovereign world spectacle awaiting scheduled chronological authorization."
  }
];

export const HALLOWEEN_EVENT_DETAILS: EventDetail = {
  id: "halloween-2026",
  title: "Halloween 2026: Haunted Ocean Realm",
  tagline: "A monumental gothic ocean celebration brought to a glorious conclusion—cut short only by an ominous, uninvited alien strike from the deep cosmos.",
  date: "October 2026",
  status: "Completed & Preserved",
  heroImage: "/assets/events/halloween-2026/2026-11-01_22.44.24_4K.webp",
  overview: [
    "Constructed exclusively for the Halloween 2026 festival, the Haunted Ocean Realm stands at oceanic coordinates X: 7000, Z: 6500 as one of the most celebrated engineering and community triumphs in Uge Appage SMP Season 3 history. Ten dedicated master-builder arenas encircled a grand Blackstone Sanctum, illuminated by dancing soul flames, bubbling witch cauldrons, and the shared creativity of our citizens.",
    "The festival concluded with resounding success. Players gathered to celebrate the completion of stunning builds, crowning a magnificent season of teamwork, creativity, and festive joy that echoed across the server.",
    "Yet, the tranquility of the celebration was not permitted to endure. In the festival's closing moments, the skies turned violently blood-red as an unpredicted extraterrestrial bombardment pierced the atmosphere without warning. A cataclysmic celestial strike smashed directly into the realm, tearing through the clouds with a deafening sonic boom, searing lightning bolts, and leaving behind a smoldering, alien-carved crater of Crying Obsidian and otherworldly energy.",
    "While the festival ended in glorious triumph, this mysterious alien strike served as an ominous harbinger—a subtle, chilling prelude to the deep extraterrestrial threats looming on the horizon. The colosseum and the eerie strike zone remain permanently preserved, accessible to all adventurers at any time through the official Event Manager stationed at the Grand Hub."
  ],
  stats: [
    { label: "Competitor Plots", value: "10 Dedicated 32x32", icon: "🏛️" },
    { label: "Ocean Coordinates", value: "7000, 64, 6500", icon: "📍" },
    { label: "Seabed Depth", value: "Y: 25 Subsea", icon: "⚓" },
    { label: "Festival Outcome", value: "Grand Victory", icon: "🎃" },
    { label: "Realm Status", value: "Permanently Preserved", icon: "💎" },
    { label: "Access Point", value: "Event Manager NPC", icon: "🎩" }
  ],
  lore: [
    {
      heading: "The Ocean Colosseum Architecture",
      text: "Designed so that any observer at the central hub altar possesses a commanding 360° panoramic view of all 10 competitor plots. The outer boardwalk features protective Jack-o'-Lantern and Soul Lantern lamp posts illuminating the dark waters below.",
      icon: "🏰"
    },
    {
      heading: "A Triumphant Halloween Festival",
      text: "The event united the realm's finest builders in a high-spirited Halloween showcase. Festivities concluded in grand celebration, celebrating the creativity, dedication, and architectural prowess of our server community.",
      icon: "🎃"
    },
    {
      heading: "The Cataclysmic Alien Strike",
      text: "The celebration was suddenly shattered as extraterrestrial forces struck the outer perimeter. A terrifying 60-second red-shift warning preceded a celestial bombardment that carved an otherworldly crater deep into the terrain.",
      icon: "☄️"
    },
    {
      heading: "Sovereign Ingress & Return Transit",
      text: "Two synchronized Event Manager NPCs in elegant black tuxedo suits link the Capital Grand Hub with the Ocean Arena, providing instant, cinematic two-way teleportation for all citizens.",
      icon: "🚪"
    }
  ],
  gallery: [
    {
      src: "/assets/events/halloween-2026/2026-11-01_22.44.24_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_22.44.24_4K.webp",
      title: "Gothic Altar & Central Sanctum",
      category: "citadel",
      caption: "Panoramic aerial perspective of the Blackstone Altar, Lodestone pedestal, and surrounding 10 builder plots in the ocean.",
      photographer: "Jabuu.x (Cinematic)"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_22.37.19_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_22.37.19_4K.webp",
      title: "Abyssal Midnight Horizon",
      category: "citadel",
      caption: "Atmospheric view of the maritime colosseum beneath the perpetual thunderstorm.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_22.44.11_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_22.44.11_4K.webp",
      title: "Witch Cauldrons & Soul Flames",
      category: "citadel",
      caption: "Detailed inspection of the ritual cauldrons, soul campfires, and polished blackstone dais.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_22.47.43_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_22.47.43_4K.webp",
      title: "Deep Sea Support Pillars",
      category: "architecture",
      caption: "Structural foundation beams rooted 40 blocks deep into the seabed (Y: 25).",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_22.55.13_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_22.55.13_4K.webp",
      title: "360° Symmetrical Plot Grid",
      category: "architecture",
      caption: "Geometric overhead view displaying the 10 distinct 32x32 builder territories.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_23.04.32_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_23.04.32_4K.webp",
      title: "Boardwalk Lantern Illumination",
      category: "architecture",
      caption: "Outer boardwalk railing with Jack-o'-Lanterns and Soul Lanterns casting eerie reflections.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_23.19.23_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_23.19.23_4K.webp",
      title: "The Celestial Incursion Descent",
      category: "strike",
      caption: "Extraterrestrial projectile piercing the night sky with blazing fire sparks and volcanic soul smoke.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_23.19.49_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_23.19.49_4K.webp",
      title: "Atmospheric Red Shift Warning",
      category: "strike",
      caption: "Blood-red worldborder vignette and ominous darkness engulfing the quadrant prior to the strike.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_23.20.44_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_23.20.44_4K.webp",
      title: "Alien Strike Ground Zero",
      category: "core",
      caption: "Instant parabolic impact crater carved smoothly into the terrain by the alien blast.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_23.21.37_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_23.21.37_4K.webp",
      title: "Extraterrestrial Meteor Spire",
      category: "core",
      caption: "Amethyst Monolith, Lodestone, and Crying Obsidian core pulsating with unearthly cosmic energy.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_23.23.19_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_23.23.19_4K.webp",
      title: "Natural Terrain Blending",
      category: "core",
      caption: "Smooth parabolic bowl carving naturally integrating with the surrounding hillside.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_23.25.11_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_23.25.11_4K.webp",
      title: "Sculk Catalysts & Eternal Fire",
      category: "core",
      caption: "Simmering crater floor with blue soul fire and sculk tendrils spreading from the impact core.",
      photographer: "Jabuu.x"
    },
    {
      src: "/assets/events/halloween-2026/2026-11-01_23.40.17_4K.webp",
      thumb: "/assets/events/halloween-2026/thumbs/2026-11-01_23.40.17_4K.webp",
      title: "Event Manager In-Game Waypoint",
      category: "citadel",
      caption: "The elegant suit-clad gentleman stationed at the ocean altar offering instant return travel.",
      photographer: "Jabuu.x"
    }
  ]
};
